// ============================================
// UTILITY FUNCTIONS — port z js/utils.js (1:1 logika)
// Wszystkie funkcje pure (poza haptic/copyToClipboard które dotykają BOM)
// ============================================

import type { Timestamp } from 'firebase/firestore';

type DateLike = Date | Timestamp | { toDate: () => Date };

// ── Formatowanie walut ────────────────────────
const currencyFmt = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const currencyCompactFmt = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** Grosze → "1 234,56 zł" */
export function formatCurrency(amountInGrosze: number): string {
  return currencyFmt.format(amountInGrosze / 100);
}

/** Grosze kompaktowo → "1,2 tys. zł" */
export function formatCurrencyCompact(amountInGrosze: number): string {
  if (Math.abs(amountInGrosze) >= 100000) {
    return currencyCompactFmt.format(amountInGrosze / 100);
  }
  return formatCurrency(amountInGrosze);
}

/** "12,50" / "12.50" / "1 250,99" → grosze (integer) lub null jeśli invalid */
export function parseAmountInput(str: string): number | null {
  const cleaned = str.replace(/\s/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  if (isNaN(num) || num <= 0) return null;
  if (num > 9999999.99) return null;
  return Math.round(num * 100);
}

export function formatAmountForInput(amountInGrosze: number): string {
  return (amountInGrosze / 100).toFixed(2).replace('.', ',');
}

// ── Daty ──────────────────────────────────────
const dateFmt = new Intl.DateTimeFormat('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' });
const dateFmtShort = new Intl.DateTimeFormat('pl-PL', { day: 'numeric', month: 'short' });
const monthFmt = new Intl.DateTimeFormat('pl-PL', { month: 'long', year: 'numeric' });

const toDate = (date: DateLike): Date =>
  date instanceof Date ? date : 'toDate' in date ? date.toDate() : new Date(date as unknown as string);

export function formatDate(date: DateLike): string {
  return dateFmt.format(toDate(date));
}

export function formatDateShort(date: DateLike): string {
  return dateFmtShort.format(toDate(date));
}

export function formatMonth(date: DateLike): string {
  const raw = monthFmt.format(toDate(date));
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function formatDayHeader(date: DateLike): string {
  const d = toDate(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);

  if (target.getTime() === today.getTime()) return 'Dziś';
  if (target.getTime() === yesterday.getTime()) return 'Wczoraj';
  if (target.getFullYear() === today.getFullYear()) {
    return new Intl.DateTimeFormat('pl-PL', { day: 'numeric', month: 'long' }).format(d);
  }
  return formatDate(d);
}

export function getMonthBounds(year: number, month: number): { start: Date; end: Date } {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

export function addMonths(date: Date, n: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}

// ── ID / kody ─────────────────────────────────
export function generateId(prefix = 'id'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function generateFamilyCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // bez I, O, 0, 1
  let code = '';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export function formatInviteCode(code: string): string {
  return code.slice(0, 3) + '-' + code.slice(3, 6);
}

// ── Bezpieczeństwo ────────────────────────────
export function escHtml(str: unknown): string {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function safeColor(color: string, fallback = 'currentColor'): string {
  if (color === 'currentColor') return color;
  return /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(color) ? color : fallback;
}

// ── UI helpers ────────────────────────────────
export function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

const WALLET_COLORS = [
  '#E53935', '#D81B60', '#8E24AA', '#5E35B1',
  '#1E88E5', '#00897B', '#43A047', '#F4511E',
  '#FB8C00', '#F6BF26', '#039BE5', '#00ACC1',
];

export function getWalletColorForIndex(index: number): string {
  return WALLET_COLORS[index % WALLET_COLORS.length]!;
}

export function getBudgetColor(percent: number): 'danger' | 'warning' | '' {
  if (percent >= 100) return 'danger';
  if (percent >= 80) return 'warning';
  return '';
}

// ── Generics ──────────────────────────────────
export function debounce<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delay = 300,
): (...args: TArgs) => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// ── Transakcje ────────────────────────────────
export interface TransactionLike {
  amount: number;
  type?: 'income' | 'expense' | 'transfer';
  date: DateLike;
}

export function groupTransactionsByDay<T extends TransactionLike>(
  transactions: T[],
): { date: Date; transactions: T[] }[] {
  const groups = new Map<string, { date: Date; transactions: T[] }>();
  for (const tx of transactions) {
    const d = toDate(tx.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let group = groups.get(key);
    if (!group) {
      group = { date: d, transactions: [] };
      groups.set(key, group);
    }
    group.transactions.push(tx);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([, v]) => v);
}

export function sumTransactions<T extends TransactionLike>(
  transactions: T[],
  type?: TransactionLike['type'],
): number {
  return transactions
    .filter((tx) => !type || tx.type === type)
    .reduce((sum, tx) => sum + tx.amount, 0);
}

// ── Walidatory ────────────────────────────────
export function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export interface PasswordValidation {
  valid: boolean;
  strength: 'weak' | 'medium' | 'strong';
}

export function validatePassword(pwd: string): PasswordValidation {
  const valid = pwd.length >= 6;
  const strength: PasswordValidation['strength'] =
    pwd.length >= 12 && /[A-Z]/.test(pwd) && /[0-9]/.test(pwd)
      ? 'strong'
      : pwd.length >= 8
      ? 'medium'
      : 'weak';
  return { valid, strength };
}

export function validateInviteCode(code: string): string | null {
  const clean = code.replace(/[-\s]/g, '').toUpperCase();
  return /^[A-Z0-9]{6}$/.test(clean) ? clean : null;
}

// ── Firestore errors → PL ─────────────────────
const FIRESTORE_ERRORS: Record<string, string> = {
  'permission-denied': 'Brak uprawnień do wykonania tej operacji.',
  unavailable: 'Brak połączenia z serwerem. Sprawdź internet.',
  'not-found': 'Nie znaleziono żądanego zasobu.',
  'already-exists': 'Ten zasób już istnieje.',
  'resource-exhausted': 'Przekroczono limit zapytań. Spróbuj za chwilę.',
};

export function handleFirestoreError(code: string): string {
  return FIRESTORE_ERRORS[code] ?? 'Wystąpił błąd. Spróbuj ponownie.';
}

// ── Haptic feedback ───────────────────────────
type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'error';

export function haptic(type: HapticType = 'light'): void {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  const patterns: Record<HapticType, number | number[]> = {
    light: 10,
    medium: 20,
    heavy: 30,
    success: [15, 50, 15],
    error: [40, 30, 40],
  };
  try {
    navigator.vibrate(patterns[type]);
  } catch {
    /* no-op */
  }
}

// ── Sparkline ─────────────────────────────────
export interface SparklineOptions {
  w?: number;
  h?: number;
  color?: string;
}

/** Zwraca obiekt do renderu jako React SVG (zamiast HTML stringa jak w vanilla) */
export function sparklinePoints(
  values: number[],
  { w = 80, h = 24 }: Pick<SparklineOptions, 'w' | 'h'> = {},
): string | null {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = w / (values.length - 1);
  return values
    .map((v, i) => {
      const x = i * stepX;
      const y = h - ((v - min) / range) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

// ── Schowek ───────────────────────────────────
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    return true;
  }
}
